import { useContext, useMemo, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useCreateAttributeValue from "./attributesValue/useCreateAttributeValue";
import useUpdateAttributeValue from "./attributesValue/useUpdateAttributeValue";
import useDeleteAttributeValue from "./attributesValue/useDeleteAttributeValue";
import useProductConfiguration from "../useProductConfiguration";

const useAttributeValueLogic = (attributes) => {
  const {
    attributeValues,
    setAttributeValues,
    selectedAttributes,
    setSelectedAttributes,
    toggleSelectedAttributeValue,
    selectAllAttributeValues,
    clearSelectedAttributeValues,
    isAttributeValueSelected,
    selectedAttributeValues,
    getSelectedAttributeValues,
    setSelectedAttributeValues,
  } = useProductConfiguration();
  const queryClient = useQueryClient();

  // Track values being saved to handle ID assignment
  const savingValuesRef = useRef({});

  // Ref to track deleting state
  const deletingValueRef = useRef(null);

  // Auto-sync when selectedAttributes change (with fresh data)
  useEffect(() => {
    console.log(
      "🔄 useEffect triggered - selectedAttributes changed:",
      selectedAttributes.length
    );
    console.log(
      "Current selectedAttributeValues from context:",
      Object.keys(selectedAttributeValues).map((key) => ({
        [key]: selectedAttributeValues[key].length,
      }))
    );

    initializeValues();
  }, [selectedAttributes]); // This will run when selectedAttributes gets fresh data

  // Initialize the create hook with callback to handle new IDs
  const { handleSubmit } = useCreateAttributeValue({
    reset: () => {
      console.log("Create hook reset called - value saved successfully");
    },
    onValueCreated: (newAttributeValueId) => {
      console.log("New attribute value created with ID:", newAttributeValueId);

      // Find and update the saving value with its new ID
      setAttributeValues((prev) => {
        const copy = { ...prev };
        let savedAttrSlug = null;

        Object.keys(copy).forEach((attrSlug) => {
          copy[attrSlug] = copy[attrSlug].map((val) => {
            // If this value was being saved and matches our tracking
            if (val.saving && savingValuesRef.current[attrSlug] === val.name) {
              console.log(
                "Updating saved value with ID:",
                val.name,
                "->",
                newAttributeValueId
              );
              savedAttrSlug = attrSlug;
              // Remove saving flag and add the ID from response
              const { saving, ...restVal } = val;
              return {
                ...restVal,
                attribute_value_id: newAttributeValueId.toString(),
                attribute_value_name: val.name,
                attribute_value: val.name,
              };
            }
            return val;
          });
        });
        // Clear the tracking after the loop
        if (savedAttrSlug) {
          delete savingValuesRef.current[savedAttrSlug];
        }
        return copy;
      });

      // Refresh the attribute data after successful save
      queryClient.invalidateQueries({ queryKey: ["product/attribute/list"] });
      queryClient.invalidateQueries({ queryKey: ["getAttributeList"] });
    },
  });

  const { handleSubmit: handleUpdate } = useUpdateAttributeValue({
    reset: () => {
      queryClient.invalidateQueries({ queryKey: ["product/attribute/list"] });
      queryClient.invalidateQueries({ queryKey: ["getAttributeList"] });
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ["product/attribute/list"] });
      }, 100);
    },
  });

  const { handleSubmit: handleDelete } = useDeleteAttributeValue({
    onSuccess: (reqParams) => {
      // This runs AFTER the API succeeds
      const { attrSlug, valueId, attributeName } = reqParams;

      // Remove from local attributeValues
      setAttributeValues((prev) => {
        const copy = { ...prev };
        copy[attrSlug] = copy[attrSlug].filter(
          (v) => String(v.attribute_value_id) !== String(valueId)
        );
        return copy;
      });

      // Remove from selected values
      const selectedValues = getSelectedAttributeValues(attributeName);
      const updatedSelected = selectedValues.filter(
        (v) => String(v.attribute_value_id) !== String(valueId)
      );
      setSelectedAttributeValues((prev) => ({
        ...prev,
        [attributeName]: updatedSelected,
      }));

      // Remove from selectedAttributes
      setSelectedAttributes((prev) =>
        prev.map((attr) => {
          if (attr.attribute_slug === attrSlug && attr.values) {
            return {
              ...attr,
              values: attr.values.filter(
                (v) => String(v.attribute_value_id) !== String(valueId)
              ),
            };
          }
          return attr;
        })
      );

      // Clear the deleting reference
      deletingValueRef.current = null;
    },
  });

  // Initialize values function - merge database values with local unsaved values
  const initializeValues = () => {
    console.log("🚀 initializeValues called");
    console.log("selectedAttributes.length:", selectedAttributes.length);
    console.log(
      "Current selectedAttributeValues from context:",
      selectedAttributeValues
    );

    if (selectedAttributes.length > 0) {
      setAttributeValues((prev) => {
        console.log("💾 Previous attributeValues state:", prev);

        // Check if we actually need to reinitialize
        const shouldReinitialize = selectedAttributes.some(
          (attr) =>
            !prev[attr.attribute_slug] ||
            (attr.values &&
              prev[attr.attribute_slug].length !== attr.values.length)
        );

        if (!shouldReinitialize) {
          console.log("⏭️ Skipping reinitialization - no changes needed");
          return prev;
        }

        console.log("🔄 Proceeding with reinitialization");
        const newAttributeValues = {};

        selectedAttributes.forEach((attr) => {
          const existingValues = attr.values || [];
          const currentValues = prev[attr.attribute_slug] || [];

          console.log(`Processing ${attr.attribute_name}:`, {
            existingFromDB: existingValues,
            currentLocal: currentValues,
            selectedInContext:
              selectedAttributeValues[attr.attribute_name] || [],
          });

          // Only keep genuinely new unsaved values that don't exist in database
          const unsavedNewValues = currentValues.filter((localVal) => {
            // If it has an attribute_value_id, check if it already exists in the fresh database values
            if (localVal.attribute_value_id) {
              const existsInDB = existingValues.some(
                (dbVal) =>
                  String(dbVal.attribute_value_id) ===
                  String(localVal.attribute_value_id)
              );
              // Only keep it if it's NOT already in the DB (to avoid duplicates)
              return !existsInDB;
            }

            // If it's currently saving, skip it - the spinner will handle this state
            if (localVal.saving) {
              console.log(
                `Skipping local value "${localVal.name}" - currently saving`
              );
              return false;
            }

            // Check if this local value matches any database value by name
            const matchesDatabase = existingValues.some(
              (dbVal) =>
                dbVal.attribute_value_name === localVal.name ||
                dbVal.attribute_value === localVal.name
            );

            if (matchesDatabase) {
              console.log(
                `Skipping local value "${localVal.name}" - matches database value`
              );
              return false;
            }

            // Only keep if it's genuinely new and has content
            const hasValidContent =
              localVal.name && localVal.name.trim() !== "";

            console.log(`Local value "${localVal.name}":`, {
              hasValidContent,
              isGenuinelyNew: true,
              willKeep: hasValidContent,
            });

            return hasValidContent;
          });

          // Merge: fresh database values + only unique new values
          newAttributeValues[attr.attribute_slug] = [
            ...existingValues, // Fresh from database with attribute_value_id
            ...unsavedNewValues, // Only genuinely new values that aren't in DB
          ];

          console.log(`${attr.attribute_name} final result:`, {
            fromDatabase: existingValues.length,
            unsavedNew: unsavedNewValues.length,
            filtered: currentValues.length - unsavedNewValues.length,
            total: newAttributeValues[attr.attribute_slug].length,
            finalValues: newAttributeValues[attr.attribute_slug],
          });
        });

        return newAttributeValues;
      });
    }
  };

  const addValue = (attrSlug) => {
    setAttributeValues((prev) => ({
      ...prev,
      [attrSlug]: [...(prev[attrSlug] || []), { name: "", selected: false }],
    }));
  };

  const changeValue = (attrSlug, idx, value) => {
    setAttributeValues((prev) => {
      const copy = { ...prev };
      copy[attrSlug] = copy[attrSlug].map((v, i) =>
        i === idx ? { ...v, name: value } : v
      );
      return copy;
    });
  };

  const deleteValue = (attrSlug, idx) => {
    setAttributeValues((prev) => {
      const copy = { ...prev };
      copy[attrSlug] = copy[attrSlug].filter((_, i) => i !== idx);
      return copy;
    });
  };

  const saveValue = (attrSlug, idx, attribute_id) => {
    setAttributeValues((prev) => {
      const copy = { ...prev };
      const valueObj = copy[attrSlug]?.[idx];
      const value =
        valueObj?.name ??
        valueObj?.attribute_value ??
        valueObj?.attribute_value_name ??
        "";
      if (valueObj && value.trim()) {
        // Only submit to API if it's a new value (doesn't have attribute_value_id)
        if (!valueObj.attribute_value_id) {
          // Mark as saving to show different icon
          copy[attrSlug][idx] = { ...valueObj, saving: true };

          // Track this value for ID assignment
          savingValuesRef.current[attrSlug] = value;

          // Prepare data for API submission
          const submissionData = {
            attribute_id,
            attribute_value: value,
            attribute_value_name: value,
          };

          // Submit to API using the create hook
          try {
            handleSubmit(submissionData);
          } catch (error) {
            console.error("Error saving attribute value:", error);
            setAttributeValues((prev) => {
              const copy = { ...prev };
              if (copy[attrSlug] && copy[attrSlug][idx]) {
                const { saving, ...restVal } = copy[attrSlug][idx];
                copy[attrSlug][idx] = restVal;
              }
              return copy;
            });
            delete savingValuesRef.current[attrSlug];
          }
        }
      }
      return copy;
    });
  };

  // Update saved value
  const updateValue = (attrSlug, idx, attribute_id) => {
    setAttributeValues((prev) => {
      const copy = { ...prev };
      const valueObj = copy[attrSlug]?.[idx];
      const value =
        valueObj?.name ??
        valueObj?.attribute_value ??
        valueObj?.attribute_value_name ??
        "";
      // Get original value from DB
      const original =
        valueObj?.attribute_value ?? valueObj?.attribute_value_name ?? "";
      // Only call API if value is changed
      if (
        valueObj &&
        value.trim() &&
        valueObj.attribute_value_id &&
        value !== original
      ) {
        const submissionData = {
          attribute_id,
          attribute_value_id: valueObj.attribute_value_id,
          attribute_value: value,
          attribute_value_name: value,
        };
        handleUpdate(submissionData);
      }
      return copy;
    });
  };

  // Delete saved value
  const deleteSavedValue = (attrSlug, idx, attribute_id) => {
    const valueObj = attributeValues[attrSlug]?.[idx];

    if (valueObj && valueObj.attribute_value_id) {
      const currentAttr = selectedAttributes.find(
        (attr) => attr.attribute_slug === attrSlug
      );

      // Mark as deleting to show loader
      deletingValueRef.current = String(valueObj.attribute_value_id);

      // Call API to delete with normal parameters
      handleDelete({
        attribute_id,
        attribute_value_id: valueObj.attribute_value_id,
        attrSlug,
        valueId: valueObj.attribute_value_id,
        attributeName: currentAttr?.attribute_name,
      });
    }
  };

  // Helper to check if a value is being deleted
  const isDeletingValue = (valueId) => {
    return deletingValueRef.current === String(valueId);
  };

  const toggleSelected = (attributeName, valueObject) => {
    console.log("Toggling selection for:", { attributeName, valueObject });
    console.log(
      "Current selected values for",
      attributeName,
      ":",
      getSelectedAttributeValues(attributeName)
    );

    // Ensure the value object has the right structure for the selection system
    const normalizedValueObject = {
      attribute_value_id: valueObject.attribute_value_id,
      attribute_value_name:
        valueObject.attribute_value_name ||
        valueObject.attribute_value ||
        valueObject.name,
      attribute_name: attributeName,
      attribute_id: valueObject.attribute_id,
      // Add any other properties that might be needed
      ...valueObject,
    };

    console.log("Normalized value object:", normalizedValueObject);
    toggleSelectedAttributeValue(attributeName, normalizedValueObject);

    // Log the result after toggling
    setTimeout(() => {
      console.log(
        "After toggle, selected values:",
        getSelectedAttributeValues(attributeName)
      );
    }, 0);
  };

  const selectAllForAttr = (attributeName, checked) => {
    const currentAttr = selectedAttributes.find(
      (attr) => attr.attribute_name === attributeName
    );
    if (currentAttr) {
      // Get all values: both from DB and local state
      const attrSlug = currentAttr.attribute_slug;
      const allLocalValues = attributeValues[attrSlug] || [];
      
      // Filter to get only valid values (with IDs, excluding unsaved/saving)
      const validValues = allLocalValues.filter(
        (val) => val.attribute_value_id && !val.saving
      );

      if (checked) {
        selectAllAttributeValues(attributeName, validValues);
      } else {
        clearSelectedAttributeValues(attributeName);
      }
    }
  };

  const selectAllGlobal = (checked) => {
    selectedAttributes.forEach((attr) => {
      const attrSlug = attr.attribute_slug;
      const allLocalValues = attributeValues[attrSlug] || [];
      
      // Only select values that have IDs and are not saving
      const validValues = allLocalValues.filter(
        (val) => val.attribute_value_id && !val.saving
      );

      if (checked && validValues.length > 0) {
        selectAllAttributeValues(attr.attribute_name, validValues);
      } else {
        clearSelectedAttributeValues(attr.attribute_name);
      }
    });
  };

  // Calculate stats for individual attribute
  const getAttrStats = (attributeName) => {
    const currentAttr = selectedAttributes.find(
      (attr) => attr.attribute_name === attributeName
    );

    const attrSlug = currentAttr?.attribute_slug;
    const allLocalValues = attributeValues[attrSlug] || [];

    // Only count values that have attribute_value_id and are not saving
    // (unsaved values without IDs should not be counted)
    const validValues = allLocalValues.filter(
      (val) => val.attribute_value_id && !val.saving
    );

    const selectedValues = getSelectedAttributeValues(attributeName);

    const total = validValues.length;
    const selectedCount = selectedValues.length;
    const checked = total > 0 && selectedCount === total;
    const indeterminate = selectedCount > 0 && selectedCount < total;

    return {
      list: validValues,
      selectedCount,
      total,
      checked,
      indeterminate,
    };
  };

  // Calculate stats with proper dependencies
  const globalStats = useMemo(() => {
    let totalSelected = 0;
    let totalCount = 0;

    selectedAttributes.forEach((attr) => {
      const allLocalValues = attributeValues[attr.attribute_slug] || [];

      // Only count values with attribute_value_id and not saving
      const validValues = allLocalValues.filter(
        (val) => val.attribute_value_id && !val.saving
      );

      const selectedValues = getSelectedAttributeValues(attr.attribute_name);
      totalCount += validValues.length;
      totalSelected += selectedValues.length;
    });

    const allChecked = totalCount > 0 && totalSelected === totalCount;
    const allIndeterminate = totalSelected > 0 && totalSelected < totalCount;
    return {
      selected: totalSelected,
      total: totalCount,
      allChecked,
      allIndeterminate,
    };
  }, [selectedAttributes, attributeValues, selectedAttributeValues]);

  return {
    attributeValues,
    initializeValues,
    addValue,
    changeValue,
    deleteValue,
    saveValue,
    updateValue,
    deleteSavedValue,
    toggleSelected,
    selectAllForAttr,
    selectAllGlobal,
    getAttrStats,
    isAttributeValueSelected,
    getSelectedAttributeValues,
    allCounts: {
      selected: globalStats.selected,
      total: globalStats.total,
    },
    allChecked: globalStats.allChecked,
    allIndeterminate: globalStats.allIndeterminate,
    isDeletingValue,
  };
};

export default useAttributeValueLogic;
